'use client';

import { useEffect, useState } from 'react';
import {
  datadogErrorHelper,
  useHandleGqlErrorsWithSnackbar,
  getSavingsPlanDetailsAction,
  getSavingsPlansTransactions,
} from '../../../common';
import { DatadogErrorLevels } from '@bts-web/utils-next-infrastructure';
import { SavingsPlansTransactionsQuery$data } from '@bts-web/data-layer/server';
import { SavingsPlanItemType } from '../../types';

type UseSavingsPlanDetailsProps = {
  savingsPlanId: string;
};

export const useSavingsPlanDetails = ({
  savingsPlanId,
}: UseSavingsPlanDetailsProps) => {
  const [savingsPlan, setSavingsPlan] = useState<SavingsPlanItemType | null>(
    null,
  );

  const [savingsPlanTransactions, setSavingsPlanTransactions] =
    useState<SavingsPlansTransactionsQuery$data['savingsPlanTransactions']>();

  const { processErrors } = useHandleGqlErrorsWithSnackbar();

  const [isDataLoading, setIsDataLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setIsDataLoading(true);

      try {
        const [detailsResponse, transactionResponse] = await Promise.all([
          getSavingsPlanDetailsAction(savingsPlanId),
          getSavingsPlansTransactions({
            first: 5,
            states: ['FINISHED', 'FAILED', 'CONFIRMED', 'SCHEDULED'],
            savingsPlanId,
          }),
        ]);

        const { savingsPlans } = detailsResponse?.data ?? {};

        setSavingsPlan(savingsPlans?.edges?.[0]?.node ?? null);

        setSavingsPlanTransactions(
          transactionResponse?.data?.savingsPlanTransactions,
        );
      } catch (error) {
        processErrors();

        datadogErrorHelper({
          errorMessage: JSON.stringify(error),
          errorSeverity: DatadogErrorLevels.HIGH,
        });
      } finally {
        setIsDataLoading(false);
      }
    };

    fetchData();
  }, [savingsPlanId]);

  return { isDataLoading, savingsPlan, savingsPlanTransactions };
};
